import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQualitativeColor } from '../../common/color.helper';
import * as Icon from '../../components/icons';
import { InputSearch } from '../../components/inputs/InputSearch';
import { Container } from '../../components/layout/ContainerFlex';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import { ActionBar } from '../../components/nav/ActionBar';
import { Stat } from '../../components/stats/Stat';
import { StatGroup } from '../../components/stats/StatGroup';

export type DockUsageStats = {
  distinctCount: number;
  counts: [string, number][];
};

type DockUsageStatisticsPanelProps = {
  dockStats: DockUsageStats;
  hiddenDocks: Set<string>;
  toggleDockVisibility: (dock: string) => void;
  hasSearch?: boolean;
};

const DockUsageStatisticsPanel: React.FC<DockUsageStatisticsPanelProps> = ({
  dockStats,
  hiddenDocks,
  toggleDockVisibility,
  hasSearch = true,
}) => {
  const { t } = useTranslation('simulation');
  const [filterText, setFilterText] = useState('');

  // Filter docks based on search text
  const filteredDockStats = useMemo(() => {
    if (!filterText.trim()) {
      return dockStats;
    }

    const filteredCounts = dockStats.counts.filter(([dock, count]) =>
      dock.toLowerCase().includes(filterText.toLowerCase()),
    );

    return {
      distinctCount: filteredCounts.length,
      counts: filteredCounts,
    };
  }, [dockStats, filterText]);

  return (
    <Container col hasOverflowY>
      <ScreenTitle
        subtitle={t`Analyse`}
        title={t`Docks`}
        helpNavTo={'simulation/analyse/simulation-analyse'}
        icon={Icon.SimulationAnalyze}
        isSticky={true}
      />

      {hasSearch && (
        <ActionBar sticky stickyTop className="px-2 py-1">
          <InputSearch
            value={filterText}
            onChange={value => setFilterText(value)}
            placeholder={t`Filter docks...`}
            className={'w-full'}
          />
        </ActionBar>
      )}

      <StatGroup classNameStatGroupItems="!space-y-0.5">
        {filteredDockStats.counts.map(([dock, count]) => (
          <Stat
            key={dock}
            title={dock}
            value={count}
            unitOfMeasure={t`jobs`}
            inPanelMode
            isActionable
            isSelectable
            isSelected={!hiddenDocks.has(dock)}
            icon={Icon.Eye}
            legendColor={getQualitativeColor(dock, 'policy')}
            onClick={() => toggleDockVisibility(dock)}
            className={classNames('cursor-pointer')}
          />
        ))}
      </StatGroup>
    </Container>
  );
};

export default DockUsageStatisticsPanel;
